import React, { useEffect, useState } from 'react';
import { Box, FlexBox, Typography, Card, Grid, useMediaQuery, useTheme, MuiBox } from '@kis-technologies/kis-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { useActions } from 'hooks/useActions';
import { selectors as configSelectors, Configuration, Messages } from 'services/configuration';
import { actions as ticketingActions, selectors as ticketingSelectors, Event } from 'services/ticketing';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { selectors as cartSelectors, Cart } from 'services/cart';
import { selectors as mediaSelectors, MediaResponse } from 'services/media';
import { useSelector } from 'react-redux';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import Img from 'react-image';
import hash from 'object-hash';
import WillCallLogo from 'assets/will_call_icon.png';
import { useStyles } from './styled';
import { Genre } from './types';
import { Paginator } from 'components/Paginator';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';

const GenresView: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Action Creators and Selectors
  const { getEvents, setGenresConfig, setEventsConfig, resetEvents, resetSession } = useActions({
    getEvents: ticketingActions.getEvents,
    setGenresConfig: ticketingActions.setGenresConfig,
    setEventsConfig: ticketingActions.setEventsConfig,
    resetEvents: ticketingActions.resetEvents,
    resetSession: ticketingActions.resetSession
  });

  const events = useSelector(ticketingSelectors.selectEvents) || [];
  const { page, selected } = useSelector(ticketingSelectors.selectGenresConfig);
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const membershipProductInCart = useSelector(cartSelectors.selectMembershipProductInCart);
  const donationProductInCart = useSelector(cartSelectors.selectDonationProductInCart);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const genreImages = useSelector(mediaSelectors.selectGenreImages) || [];
  const fetchCompleted = useSelector(ticketingSelectors.selectFetchCompleted);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const genreSelection: Configuration['ticketing']['genreSelection'] = get(
    configuration,
    `${ticketingPath}.genreSelection`,
    {}
  );
  const genreSelectionEnabled: boolean = get(genreSelection, 'enabled', false);
  const priorityGenres: string[] = get(genreSelection, 'priorityGenres', []);
  const messages: Messages = get(genreSelection, 'messages', {});
  const header: string = get(messages, `header.${i18n.language}`, t('genres.genresMessagingHeader'));
  const subheader: string = get(messages, `subheader.${i18n.language}`, '');
  const backButton = get(messages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const nextButton = get(messages, `buttons.${i18n.language}[1]`, t('commonText.next'));
  const productAvailabilityAndSelection: Configuration['ticketing']['productAvailabilityAndSelection'] = get(
    configuration,
    `${ticketingPath}.productAvailabilityAndSelection`,
    {}
  );
  const autoLaunchProduct: string[] = get(productAvailabilityAndSelection, 'autoLaunchProduct', []);
  const productsToExcludeFromProductSelection: string[] = get(
    productAvailabilityAndSelection,
    'productsToExcludeFromProductSelection',
    []
  );
  const touchForward = get(configuration, 'configuration.globalSettings.touchForward.purchaseFlow.enabled');
  const palette = get(configuration, 'configuration.globalSettings.palette');
  const productLabel = get(configuration, `configuration.labels.product`);
  const singularProductLabel = get(productLabel, `singular.${i18n.language}`, t('commonText.product'));
  const pluralProductLabel = get(productLabel, `plural.${i18n.language}`, t('commonText.product_plural'));
  const { clientId } = kiosk.venue;
  const previewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/genre_images/`;

  const [selectedGenre, setSelectedGenre] = useState<string>(touchForward ? '' : selected);
  const [currentPage, setCurrentPage] = useState(page);
  const [genresToDisplay, setGenresToDisplay] = useState<Genre[]>([]);
  const [uiLoading, setUILoading] = useState<boolean>(true);
  const [showErrorOverlay, setShowErrorOverlay] = useState<boolean>(false);

  const classes = useStyles();

  // Determine which direction to go if skipping over this page
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { from?: string };
  const followingPages = ['/ticketing/events', '/ticketing/tiers'];
  const navigatingBack = state && followingPages.includes(state?.from ?? '');

  useEffect(() => {
    if (!fetchCompleted.events) getEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fetchCompleted.events && events.length) {
      // determine if there is a product available that is configured to auto launch
      const productToAutoLaunch = getProductToAutoLaunch();
      if (productToAutoLaunch) {
        handleAutoLaunchProduct(productToAutoLaunch);
      } else if (!genreSelectionEnabled) {
        skipGenreSelection();
      } else {
        const genresOfProducts = getGenresOfProducts();
        if (genresOfProducts.length === 0) {
          setShowErrorOverlay(true);
          setUILoading(false);
        } else {
          setGenresToDisplay(genresOfProducts.sort((a, b) => a.priority - b.priority));
        }
      }
    } else if (fetchCompleted.events && !events.length) {
      // skip to EventsView where it will display a message for no events available
      skipGenreSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompleted.events]);

  useEffect(() => {
    if (genresToDisplay.length === 1) {
      skipGenreSelection();
    } else if (genresToDisplay.length > 1) {
      setUILoading(false);
    }
  }, [genresToDisplay]);

  useEffect(() => {
    if (selectedGenre && (touchForward || (genresToDisplay.length === 1 && !navigatingBack))) {
      onNextPageClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  /**
   * Returns a product to auto launch if one is available, or undefined if none are found.
   *
   * - If there is a cart or no autoLaunchProduct(s) configured, it will return undefined.
   */
  const getProductToAutoLaunch = (): Event | undefined => {
    let productToAutoLaunch: Event | undefined;

    if (autoLaunchProduct.length && !cart) {
      // determine if any of the autoLaunchProduct(s) are in the events list
      autoLaunchProduct.some((product: string) => {
        productToAutoLaunch = events.find((e) => e.name === product);
        return productToAutoLaunch ? true : false;
      });
    }

    return productToAutoLaunch;
  };

  /**
   * Navigates to the prev page if navigating back, otherwise sets the events
   * config to auto select the product and navigates to tier selection.
   */
  const handleAutoLaunchProduct = (productToAutoLaunch: Event) => {
    if (navigatingBack) {
      onPrevPageClick();
    } else {
      setEventsConfig({
        selected: (productToAutoLaunch as Event).id,
        page: currentPage,
        isAutoLaunch: true
      });

      setTimeout(() => {
        navigate('/ticketing/events', { state: { from: location.pathname } });
      }, 0);
    }
  };

  /**
   * Returns an array of genres with the number of products available and a priority for each genre.
   *
   * - If there are products in the exclusion list, it will filter them out.
   * - If a membership or donation product is in the cart, it will filter out products of that type.
   * - If there is a product without a genre, it will return an empty array.
   */
  const getGenresOfProducts = (): Genre[] => {
    let filteredProducts = [...events];

    // filter out products that are in this exclusion list
    if (productsToExcludeFromProductSelection.length) {
      filteredProducts = filteredProducts.filter((p) => {
        return !productsToExcludeFromProductSelection.includes(p.name);
      });
    }

    // if a membership is in the cart: filter out products of that type
    if (membershipProductInCart && membershipProductInCart.type) {
      filteredProducts = filteredProducts.filter((p) => p.type !== membershipProductInCart.type);
    }
    // if a donation is in the cart: filter out products of that type
    if (donationProductInCart && donationProductInCart.type) {
      filteredProducts = filteredProducts.filter((p) => p.type !== donationProductInCart.type);
    }

    // if there is a product without a genre, return empty array
    const productHasNoGenre = filteredProducts.some((p) => !p.genre);
    if (productHasNoGenre) return [];

    const tempGenres = filteredProducts.reduce((acc: Genre[], product) => {
      const genreIndex = acc.findIndex((g) => g.name === product.genre);
      if (genreIndex > -1) {
        acc[genreIndex].productsQty += 1;
      } else {
        const priorityIndex = priorityGenres.indexOf(product.genre || '');
        acc.push({
          name: product.genre || '',
          alias: product.genreAlias,
          productsQty: 1,
          priority: priorityIndex !== -1 ? priorityIndex : priorityGenres.length
        });
      }
      return acc;
    }, []);

    return tempGenres;
  };

  /**
   * Navigates to the previous page if navigating back,
   * or auto selects the genre if there is only one,
   * or navigates to the next page (EventsView).
   */
  const skipGenreSelection = () => {
    if (navigatingBack) {
      onPrevPageClick();
    } else if (genresToDisplay.length === 1) {
      setSelectedGenre(genresToDisplay[0].name);
    } else {
      navigate('/ticketing/events');
    }
  };

  const onPrevPageClick = () => {
    if (cart) {
      navigate('/ticketing/cart', { state: { from: location.pathname } });
    } else {
      navigateToPrevPage();
    }
  };

  const navigateToPrevPage = () => {
    // sets the genre page config to have nothing selected and be on first page
    setGenresConfig({ page: 1, selected: '' });
    resetEvents();
    let prevPagePath = '/ticketing/start-purchase';
    if (ticketingPath === 'cityPass.ticketing') {
      resetSession();
      prevPagePath = '/ticketing/citypass';
    }
    // re-routes to the previous page (StartPurchaseView)
    navigate(prevPagePath, { state: { from: location.pathname } });
  };

  const onNextPageClick = () => {
    // sets the genre page config to have the selected page and genre
    setGenresConfig({ page: currentPage, selected: selectedGenre });

    // re-routes to the next page (EventsView)
    navigate(`/ticketing/events`);
  };

  const getGenreImageUrl = (genreName: string) => {
    const imgObj =
      genreName &&
      genreImages.find((item: MediaResponse) => {
        return item.name === hash(genreName.replace(new RegExp('/', 'g'), ' '));
      });
    if (imgObj && imgObj.s3Name) {
      return previewUrl.concat(imgObj.s3Name);
    }

    return '';
  };

  const themes = useTheme();
  const matchPortrait = useMediaQuery(themes.breakpoints.down('lg'));

  if (uiLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  if (showErrorOverlay) {
    return (
      <ActionOverlay
        title={t('saleError.eventAPIHeader')}
        description={t('genres.notSupported')}
        confirmText={t('commonText.back')}
        onConfirm={onPrevPageClick}
        height={500}
        style={{ background: 'rgba(255,255,255,0.95)' }}
      />
    );
  }

  return (
    <>
      {genresToDisplay && genresToDisplay.length ? (
        <>
          <Grid className={classes.genresViewOuterContainer}>
            <Grid container direction="column" className={classes.genresViewInnerContainer}>
              {/* Title */}
              <Box fullWidth flexOverride="none" margin="0 0 5">
                <Typography variant="h2" fontSize={52}>
                  {header}
                </Typography>
                <Typography fontSize={30}>{subheader || <>&nbsp;</>}</Typography>
              </Box>
              {/* List of Genre cards, wrapped in paginator. Paginates if there are more than 4 genres */}
              <Paginator
                items={genresToDisplay}
                itemsPerPage={4}
                page={currentPage}
                onPageChange={(newPage) => setCurrentPage(newPage)}
              >
                {({ items }) => (
                  <div className={classes.genresCardContainer}>
                    {items.map((genre, idx) => (
                      <MuiBox key={idx} className={classes.genresCardBox}>
                        {/* Genre Card */}
                        <Card
                          hasBorderRadius
                          raised
                          fullHeight
                          hasBorder={genre.name === selectedGenre}
                          borderColor={palette?.primary}
                        >
                          {/* Selects genre name on click of card */}
                          <Card.ActionArea onClick={() => setSelectedGenre(genre.name)}>
                            <Grid container className={classes.genresCardContent}>
                              <Grid item className={classes.genresCardImage}>
                                <Img
                                  src={[getGenreImageUrl(genre.name), `${previewUrl}default.png`]}
                                  className={classes.genresViewImage}
                                  unloader={
                                    <img src={WillCallLogo} alt="Genre Logo" className={classes.genresDefaultImage} />
                                  }
                                />
                              </Grid>
                              <Grid item>
                                <MuiBox className={classes.genresHorizontalDivider}>
                                  <Card.Divider />
                                </MuiBox>
                                <MuiBox className={classes.genresVerticalDivider} />
                              </Grid>
                              <Grid container item className={classes.genresCardText} direction="column">
                                <FlexBox justify="between" align="center">
                                  <Typography
                                    fontSize={28}
                                    fontWeight={400}
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: matchPortrait ? 3 : 2,
                                      WebkitBoxOrient: 'vertical'
                                    }}
                                  >
                                    {genre.alias || genre.name}
                                  </Typography>
                                </FlexBox>
                                <FlexBox justify="between" align="center">
                                  {genre.productsQty && (
                                    <Typography fontSize={24} fontWeight={400}>
                                      {genre.productsQty === 1
                                        ? t('genres.genresProductAvailable', {
                                          count: genre.productsQty,
                                          label: singularProductLabel
                                        })
                                        : t('genres.genresProductAvailable_plural', {
                                          count: genre.productsQty,
                                          label: pluralProductLabel
                                        })}
                                    </Typography>
                                  )}
                                </FlexBox>
                              </Grid>
                            </Grid>
                          </Card.ActionArea>
                        </Card>
                      </MuiBox>
                    ))}
                  </div>
                )}
              </Paginator>
            </Grid>
          </Grid>
          {/* Footer */}
          <Footer
            onPrevClick={onPrevPageClick}
            onNextClick={onNextPageClick}
            prevText={backButton}
            nextText={touchForward ? undefined : nextButton}
            isNextDisabled={!selectedGenre}
          />
        </>
      ) : null}
    </>
  );
};

export default GenresView;
